/**
 * When a session ends by itself.
 *
 * Two mechanisms, deliberately different on different devices.
 *
 * The one that always applies is the idle window: the server mints a refresh
 * token that lives `AVM_SESSION_IDLE_MINUTES` and rotates it on every use, so
 * a session that is being used keeps sliding forward and one that is not runs
 * out. The screen counts the same window so it can warn before that happens
 * rather than dropping somebody mid-sentence.
 *
 * The other is closing the application. The refresh cookie is a session
 * cookie, so a closed browser drops it - but on a desktop the last tab can go
 * without the browser going, and a beacon on the way out covers that. On a
 * phone or a tablet it must not: `pagehide` fires when the engineer switches
 * to the camera or takes a call, and a beacon there would sign them out in the
 * middle of their own work. There, the window and the cookie are enough.
 */

const KLJUC_TABOVA = "avm.sesija.tabovi";

/** Tabs that have not checked in for this long are treated as gone. */
const ZASTARJELI_TAB_MS = 60_000;

type Slusatelj = () => void;

const slusatelji = new Set<Slusatelj>();

/**
 * Report that the person is doing something the browser cannot see.
 *
 * A scan or an upload can take minutes with no click, no key and no scroll.
 * Without this the idle timer would count that as an empty room.
 */
export function oznaciAktivnost(): void {
  for (const slusatelj of slusatelji) slusatelj();
}

export function pratiAktivnost(slusatelj: Slusatelj): () => void {
  slusatelji.add(slusatelj);
  return () => {
    slusatelji.delete(slusatelj);
  };
}

/**
 * True where `pagehide` means "the user switched apps" as often as "the user
 * left".
 *
 * A coarse pointer is the signal, and an unreadable one counts as coarse. The
 * two mistakes are not equal: treating a desktop as a phone only means the
 * session waits for the idle window, while treating a phone as a desktop signs
 * an engineer out for answering the phone.
 */
export function jeDodirniUredaj(): boolean {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return true;
  }
}

function procitajTabove(): Record<string, number> {
  try {
    const sirovo = localStorage.getItem(KLJUC_TABOVA);
    const razlucen: unknown = sirovo ? JSON.parse(sirovo) : {};
    if (!razlucen || typeof razlucen !== "object") return {};
    const sada = Date.now();
    return Object.fromEntries(
      Object.entries(razlucen as Record<string, number>).filter(
        ([, kada]) => typeof kada === "number" && sada - kada < ZASTARJELI_TAB_MS,
      ),
    );
  } catch {
    return {};
  }
}

function zapisiTabove(tabovi: Record<string, number>): void {
  try {
    localStorage.setItem(KLJUC_TABOVA, JSON.stringify(tabovi));
  } catch {
    // A browser that refuses storage still gets the idle window, which is the
    // guarantee that matters. It only loses the "was that the last tab" answer.
  }
}

/**
 * Register this tab and keep it checked in.
 *
 * Returns a function that removes the tab and answers whether it was the last
 * one - which is what decides whether closing it ends the session for the
 * account or just closes a window.
 */
export function registrirajTab(id: string): {
  odjaviTab: () => boolean;
  otkucaj: () => void;
} {
  const otkucaj = () => {
    zapisiTabove({ ...procitajTabove(), [id]: Date.now() });
  };
  otkucaj();
  return {
    otkucaj,
    odjaviTab: () => {
      const preostali = procitajTabove();
      delete preostali[id];
      zapisiTabove(preostali);
      return Object.keys(preostali).length === 0;
    },
  };
}

/**
 * Tell the server the session is over, on the way out of the page.
 *
 * `sendBeacon` and not a request: the page is going, and a normal call would
 * be cancelled with it. Nothing here waits for or reads an answer.
 */
export function odjaviBeaconom(): void {
  try {
    navigator.sendBeacon?.("/api/auth/logout", new Blob([], { type: "text/plain" }));
  } catch {
    // Best effort by definition. The idle window still closes the session.
  }
}
